/**
 * Safe Area Insets Hook
 * Provides consistent safe area inset values for devices with notches, rounded corners, etc.
 * 
 * Safe area insets ensure content is not obscured by:
 * - Device notches (iPhone X+)
 * - Home indicators (iPhone X+)
 * - Rounded corners
 * - System UI elements
 */

import { useMemo } from 'react';
import { SxProps, Theme } from '@mui/material';

export interface SafeAreaInsets {
  top: string;
  bottom: string;
  left: string;
  right: string;
}

/**
 * Hook to get safe area inset values
 * Returns CSS env() values for safe area insets
 */
export const useSafeArea = (): SafeAreaInsets => {
  return useMemo(() => ({
    top: 'env(safe-area-inset-top)',
    bottom: 'env(safe-area-inset-bottom)',
    left: 'env(safe-area-inset-left)',
    right: 'env(safe-area-inset-right)',
  }), []);
};

/**
 * Get safe area styles for fixed top elements (AppBar, headers)
 * Applies top inset and left/right insets for full-width elements
 */
export const getSafeAreaTop = (): SxProps<Theme> => ({
  paddingTop: 'env(safe-area-inset-top)',
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
});

/**
 * Get safe area styles for fixed bottom elements (bottom nav, FABs)
 * Applies bottom inset and left/right insets for full-width elements
 */
export const getSafeAreaBottom = (): SxProps<Theme> => ({
  paddingBottom: 'env(safe-area-inset-bottom)',
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
});

/**
 * Get safe area styles for fixed elements on all sides (modals, dialogs)
 * Applies all safe area insets
 */
export const getSafeAreaAll = (): SxProps<Theme> => ({
  paddingTop: 'env(safe-area-inset-top)',
  paddingBottom: 'env(safe-area-inset-bottom)',
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
});

/**
 * Get safe area styles for sticky top elements
 * Applies top inset only
 */
export const getSafeAreaStickyTop = (): SxProps<Theme> => ({
  paddingTop: 'env(safe-area-inset-top)',
});

/**
 * Get safe area styles for content that needs bottom spacing
 * Useful for content areas above fixed bottom navigation
 */
export const getSafeAreaContentBottom = (additionalPadding: string = '80px'): SxProps<Theme> => ({
  paddingBottom: `calc(${additionalPadding} + env(safe-area-inset-bottom))`,
});

/**
 * Get safe area styles for content that needs top spacing
 * Useful for content areas below fixed headers
 */
export const getSafeAreaContentTop = (additionalPadding: string = '64px'): SxProps<Theme> => ({
  paddingTop: `calc(${additionalPadding} + env(safe-area-inset-top))`,
});

/**
 * Get safe area styles for FAB (Floating Action Button)
 * Applies bottom and right insets
 */
export const getSafeAreaFAB = (): SxProps<Theme> => ({
  bottom: 'env(safe-area-inset-bottom)',
  right: 'env(safe-area-inset-right)',
});

